import { createAsyncThunk } from '@reduxjs/toolkit';
import type { RootState, AsyncThunkConfig, CreateSessionPayload } from '../types';
import { api } from '../../services/api';
import * as messagesActions from '../slices/messagesSlice';
import * as speechActions from '../slices/speechSlice';
import * as avatarActions from '../slices/avatarSlice';
import * as sessionsActions from '../slices/sessionsSlice';
import * as diagnosisActions from '../slices/diagnosisSlice';
import { parseScoringLogic } from '../../utils/scoringUtils';
import type { ExchangeTimingMetadata } from '../../services/exchangeTiming';

// Initialize application
export const initializeApp = createAsyncThunk<
  { apiStatus: any; fields: any[] },
  void,
  AsyncThunkConfig
>('app/initialize', async (_, { rejectWithValue, dispatch }) => {
  try {
    // Load API status and fields in parallel
    const [apiStatus, fields] = await Promise.all([
      api.getApiKeyStatus(),
      api.getFields(),
    ]);
    
    return { apiStatus, fields };
  } catch (error: any) {
    console.error('Failed to initialize app:', error);
    return rejectWithValue(error.message || 'Failed to initialize application');
  }
});

// Create diagnostic session with proper cleanup
export const createDiagnosticSession = createAsyncThunk<
  { sessionId: string },
  CreateSessionPayload,
  AsyncThunkConfig
>('sessions/create', async ({ fieldId, caseId }, { getState, dispatch, rejectWithValue }) => {
  try {
    console.log('🔄 Creating new session:', { fieldId, caseId });
    
    const state = getState();
    const existingSession = state.sessions.current;
    
    // STEP 1: Clean up existing session and connections ONLY if different
    if (existingSession && (existingSession.fieldId !== fieldId || existingSession.caseId !== caseId)) {
      console.log('🧹 Cleaning up existing session:', existingSession.sessionId);
      
      // Disconnect WebSocket
      dispatch({ type: 'websocket/disconnect' });
      
      // Reset speech recognition
      dispatch({ type: 'speech/reset' });
      
      // Disconnect avatar
      dispatch({ type: 'avatar/disconnect' });
      
      // Clear messages and reset processing state
      dispatch(messagesActions.clearMessages());
      dispatch(messagesActions.setProcessing(false));
      
      // Clear current session
      dispatch(sessionsActions.clearCurrentSession());
      
      // Wait briefly for cleanup
      await new Promise(resolve => setTimeout(resolve, 200));
    } else if (existingSession && existingSession.fieldId === fieldId && existingSession.caseId === caseId) {
      // Same case selected - just ensure we're in good state
      console.log('📍 Same case selected, ensuring clean state');
      dispatch(messagesActions.setProcessing(false));
      dispatch(messagesActions.cleanupThinkingMessages());
      return { sessionId: existingSession.sessionId }; // Return existing session ID
    }
    
    console.log('✅ Cleanup completed, creating new session...');
    
    // STEP 2: Create new session via API
    const response = await api.createSession(fieldId, caseId);
    console.log('📡 Session API response:', response);
    
    // STEP 3: Set up new session in Redux state
    dispatch(sessionsActions.createSession({
      sessionId: response.sessionId,
      fieldId,
      caseId,
    }));
    
    // STEP 4: Reset all related state
    dispatch(messagesActions.clearMessages());
    dispatch(messagesActions.setProcessing(false));
    
    // STEP 5: Enable features based on API status
    const apiStatus = state.app.apiStatus;
    if (apiStatus?.deepgram) {
      console.log('🎤 Enabling speech recognition');
      dispatch(speechActions.setEnabled(true));
    }
    dispatch(avatarActions.setProvider("anam"));
    dispatch(avatarActions.setEnabled(!!apiStatus?.anam));
    
    // STEP 6: Connect WebSocket to new session (no timeout check)
    console.log('🔌 Connecting WebSocket to new session:', response.sessionId);
    dispatch({
      type: 'websocket/connect',
      payload: { sessionId: response.sessionId },
    });
    
    // STEP 7: Add welcome message immediately (don't wait for WebSocket)
    const field = state.fields.items.find(f => f.field_id === fieldId);
    const welcomeMessage = `Case loaded: ${response.patientCase.title}. ` +
      `Patient is a ${response.patientCase.age}-year-old ${response.patientCase.gender} ${response.patientCase.occupation}. ` +
      `You may now begin your diagnostic interview using the ${field?.field_name || 'standard'} framework.`;
    
    dispatch(messagesActions.addSystemMessage(welcomeMessage));

    // STEP 8: Initialize scoring if case has rubric
    const caseWithScoring = response.patientCase.scoring_logic;
    const parsedScoringLogic = parseScoringLogic(caseWithScoring);

    if (parsedScoringLogic && Array.isArray(parsedScoringLogic)) {
      console.log('🏆 Initializing scoring rubric');
      dispatch(diagnosisActions.initializeScoring(parsedScoringLogic));
    }
    
    console.log('🎉 Session creation completed successfully');
    
    // Return the session ID for the component to use
    return { sessionId: response.sessionId };
    
  } catch (error: any) {
    console.error('❌ Failed to create session:', error);
    
    // Clean up on error
    dispatch(messagesActions.setProcessing(false));
    dispatch(messagesActions.forceResetProcessing());
    dispatch({ type: 'websocket/disconnect' });
    dispatch({ type: 'speech/reset' });
    dispatch({ type: 'avatar/disconnect' });
    
    return rejectWithValue(error.message || 'Failed to create session');
  }
});

// End diagnostic session
export const endDiagnosticSession = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>('sessions/end', async (_, { getState, dispatch, rejectWithValue }) => {
  // Release media even when there is no session or the session API fails.
  dispatch({ type: 'speech/reset' });
  const avatarCleanup = dispatch({ type: 'avatar/disconnect' });
  try {
    const state = getState();
    const currentSession = state.sessions.current;
    
    if (!currentSession) {
      return;
    }
    
    console.log('Ending session:', currentSession.sessionId);
    
    // End session via API
    await api.endSession(currentSession.sessionId);
    
    // Disconnect WebSocket
    dispatch({ type: 'websocket/disconnect' });
    
    // Clear messages
    dispatch(messagesActions.clearMessages());

    // Clear current session
    dispatch(sessionsActions.clearCurrentSession());
    
    console.log('Session ended successfully');
    
  } catch (error: any) {
    console.error('Failed to end session:', error);
    return rejectWithValue(error.message || 'Failed to end session');
  } finally {
    await avatarCleanup;
  }
});

// Ask question with speech integration
export const askQuestionWithSpeech = createAsyncThunk<
  void,
  { question: string; fromSpeech?: boolean; timing?: ExchangeTimingMetadata },
  AsyncThunkConfig
>('messages/askQuestion', async ({ question, fromSpeech = false, timing }, { getState, dispatch }) => {
  const state = getState();
  const currentSession = state.sessions.current;
  
  console.log('Asking question thunk:', { question, fromSpeech, hasSession: !!currentSession, isProcessing: state.messages.isProcessing });
  
  if (!currentSession || state.messages.isProcessing) {
    console.log('Cannot ask question - no session or already processing');
    return;
  }
  
  // If from speech, clear transcript
  if (fromSpeech) {
    dispatch(speechActions.clearTranscript());
  }
  
  // Send question via WebSocket
  dispatch({
    type: 'websocket/askQuestion',
    payload: {
      question,
      caseId: currentSession.caseId,
      sessionId: currentSession.sessionId,
      timing,
    },
  });
});

// Submit diagnosis
export const submitDiagnosis = createAsyncThunk<
  void,
  { diagnosis: string },
  AsyncThunkConfig
>('messages/submitDiagnosis', async ({ diagnosis }, { getState, dispatch }) => {
  const state = getState();
  const currentSession = state.sessions.current;
  
  console.log('Submitting diagnosis thunk:', { diagnosis, hasSession: !!currentSession });
  
  if (!currentSession) {
    console.log('Cannot submit diagnosis - no session');
    return;
  }
  
  // Send diagnosis via WebSocket
  dispatch({
    type: 'websocket/submitDiagnosis',
    payload: {
      diagnosis,
      caseId: currentSession.caseId,
      sessionId: currentSession.sessionId,
    },
  });
});

// Toggle speech recognition
export const toggleSpeechRecognition = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>('speech/toggleSpeech', async (_, { getState, dispatch }) => {
  const state = getState();
  
  console.log('Toggle speech thunk:', { 
    enabled: state.speech.isEnabled, 
    listening: state.speech.isListening,
    connecting: state.speech.isConnecting
  });
  
  if (!state.speech.isEnabled) {
    console.log('Speech not enabled');
    return;
  }
  
  if (state.speech.isConnecting) {
    console.log('Speech currently connecting, ignoring toggle');
    return;
  }
  
  // Dispatch the toggle action - middleware will handle the actual start/stop
  dispatch({ type: 'speech/toggle' });
});

// Connect avatar
export const connectAvatar = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>('avatar/connect', async (_, { getState, dispatch }) => {
  const state = getState();
  const currentSession = state.sessions.current;
  
  console.log('Connect avatar thunk:', { 
    hasSession: !!currentSession, 
    enabled: state.avatar.isEnabled, 
    connected: state.avatar.isConnected 
  });
  
  if (!currentSession || !state.avatar.isEnabled || state.avatar.isConnected) {
    console.log('Cannot connect avatar - conditions not met');
    return;
  }
  
  // Find the case from simple structure
  const patientCase = state.cases.itemsByField[currentSession.fieldId]?.find(
    c => c.id === currentSession.caseId
  );
  
  if (!patientCase) {
    console.error('Patient case not found for avatar connection');
    return;
  }
  
  console.log('Dispatching avatar connect action');
  dispatch({
    type: 'avatar/connect',
    payload: { patientCase },
  });
});

// Disconnect avatar
export const disconnectAvatar = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>('avatar/disconnect', async (_, { dispatch }) => {
  console.log('Disconnect avatar thunk');
  await dispatch({ type: 'avatar/disconnect' });
});

// Helper thunk to get current session with details
export const getCurrentSessionWithDetails = createAsyncThunk<
  { session: any; field: any; case: any } | null,
  void,
  AsyncThunkConfig
>('sessions/getCurrentWithDetails', async (_, { getState }) => {
  const state = getState();
  const currentSession = state.sessions.current;
  
  if (!currentSession) {
    return null;
  }
  
  // Find field from simple array
  const field = state.fields.items.find(f => f.field_id === currentSession.fieldId);
  
  // Find case from simple structure
  const patientCase = state.cases.itemsByField[currentSession.fieldId]?.find(
    c => c.id === currentSession.caseId
  );
  
  if (!field || !patientCase) {
    return null;
  }
  
  return {
    session: currentSession,
    field,
    case: patientCase,
  };
});

// Refresh field data
export const refreshFields = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>('fields/refresh', async (_, { dispatch }) => {
  // This will trigger the RTK Query to refetch
  // The actual data loading is handled by RTK Query hooks in components
  console.log('Refreshing fields data...');
});

// Refresh cases for a field
export const refreshCasesForField = createAsyncThunk<
  void,
  { fieldId: string },
  AsyncThunkConfig
>('cases/refreshForField', async ({ fieldId }, { dispatch }) => {
  // This will trigger the RTK Query to refetch
  // The actual data loading is handled by RTK Query hooks in components
  console.log(`Refreshing cases for field: ${fieldId}`);
});

// Add system message helper
export const addSystemMessage = createAsyncThunk<
  void,
  string,
  AsyncThunkConfig
>('messages/addSystem', async (message, { dispatch }) => {
  dispatch(messagesActions.addSystemMessage(message));
});

// Clear all messages
export const clearAllMessages = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>('messages/clearAll', async (_, { dispatch }) => {
  dispatch(messagesActions.clearMessages());
});

// Get field by ID helper
export const getFieldById = createAsyncThunk<
  any | null,
  string,
  AsyncThunkConfig
>('fields/getById', async (fieldId, { getState }) => {
  const state = getState();
  return state.fields.items.find(f => f.field_id === fieldId) || null;
});

// Get case by ID helper
export const getCaseById = createAsyncThunk<
  any | null,
  string,
  AsyncThunkConfig
>('cases/getById', async (caseId, { getState }) => {
  const state = getState();
  
  // Search through all fields to find the case
  for (const cases of Object.values(state.cases.itemsByField)) {
    const foundCase = cases.find(c => c.id === caseId);
    if (foundCase) return foundCase;
  }
  
  return null;
});

// Emergency cleanup thunk (for when things get really stuck)
export const emergencyCleanup = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>('app/emergencyCleanup', async (_, { dispatch }) => {
  console.log('🚨 EMERGENCY CLEANUP INITIATED');
  
  // Force disconnect everything
  dispatch({ type: 'websocket/disconnect' });
  dispatch({ type: 'speech/reset' });
  dispatch({ type: 'avatar/disconnect' });
  
  // Reset all message state
  dispatch(messagesActions.forceResetProcessing());
  dispatch(messagesActions.cleanupThinkingMessages());
  
  // Mark current session as inactive (don't clear it completely)
  dispatch(sessionsActions.markSessionInactive());
  
  console.log('🚨 EMERGENCY CLEANUP COMPLETED');
});

// Force reconnect WebSocket (for when connection is stuck)
export const forceReconnectWebSocket = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>('websocket/forceReconnect', async (_, { getState, dispatch }) => {
  const state = getState();
  const currentSession = state.sessions.current;
  
  if (!currentSession) {
    console.log('No current session to reconnect to');
    return;
  }
  
  console.log('🔌 Force reconnecting WebSocket to session:', currentSession.sessionId);
  
  // Disconnect first
  dispatch({ type: 'websocket/disconnect' });
  
  // Wait a moment
  await new Promise(resolve => setTimeout(resolve, 1000));
  
  // Reconnect
  dispatch({
    type: 'websocket/connect',
    payload: { sessionId: currentSession.sessionId },
  });
});
